import { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Settings } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const AdminSideBar = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [barPosition, setBarPosition] = useState(0);
  const location = useLocation();
  const barRef = useRef(null);
  const containerRef = useRef(null);
  const scrollableAreaRef = useRef(null);
  const scrollableContentRef = useRef(null);
  const sidebarRef = useRef(null);

  const handleMouseDown = (e) => {
    e.preventDefault();
    const container = containerRef.current;
    const bar = barRef.current;
    
    if (!container || !bar) return;

    const containerHeight = container.clientHeight;
    const barHeight = bar.clientHeight;
    const maxY = containerHeight - barHeight;

    const startY = e.clientY;
    const startBarTop = barPosition;

    function handleMouseMove(e) {
      const currentY = e.clientY;
      const deltaY = currentY - startY;
      const newPosition = Math.min(Math.max(0, startBarTop + deltaY), maxY);
      setBarPosition(newPosition);
    }

    function handleMouseUp() {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const startDragging = (e) => {
    e.preventDefault();
    const startY = e.clientY;
    const startBarPos = barPosition;
    const contentWrapper = containerRef.current;

    if (!contentWrapper) return;

    const maxScroll = contentWrapper.scrollHeight - contentWrapper.clientHeight;
    const maxBarTravel = contentWrapper.clientHeight - barRef.current.clientHeight;

    const onMouseMove = (moveEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const newPosition = startBarPos + deltaY;
      
      // Limit the bar movement
      const position = Math.min(Math.max(newPosition, 0), maxBarTravel);
      setBarPosition(position);

      // Move the content
      const scrollRatio = position / maxBarTravel;
      contentWrapper.scrollTop = scrollRatio * maxScroll;
    };

    const stopDragging = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", stopDragging);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", stopDragging);
  };

  // Add scroll handler to update bar position when scrolling
  const handleScroll = () => {
    const scrollableContent = scrollableContentRef.current;
    const sidebarHeight = sidebarRef.current.clientHeight;
    const barHeight = barRef.current.clientHeight;

    if (scrollableContent) {
      const scrollRatio = scrollableContent.scrollTop / (scrollableContent.scrollHeight - scrollableContent.clientHeight);
      const maxPosition = sidebarHeight - barHeight - 100;
      setBarPosition(scrollRatio * maxPosition);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024) {
        setIsOpen(false);
      } else {
        setIsOpen(true);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const menuItems = [
    { name: "User Account", path: "/useraccount" },
  ];

  const navItems = [
    { name: "Dashboard", path: "/dashboards" },
    { name: "Top Banner", path: "/topbannermainpage" },
    { name: "About Us", path: "/aboutusmainpage" },
    { name: "Services", path: "/servicespage" },
    { name: "News", path: "/newsmainpage" },
    { name: "Voice of Farmers", path: "/voiceoffarmersmainpage" },
    { name: "Reports", path: "/reports" },
    { name: "Communities", path: "/admincommuinitesmainpage" },
    { name: "Resources", path: "/resourcesmainpage" },
    { name: "Gallery", path: "/gallery" },
    { name: "-", path: "/resourcesmainpage" },
    { name: "-", path: "/gallery" },
  ];

  return (
    <div
      className={`min-h-screen h-full top-0 left-0 bg-white fixed transition-all duration-300 flex flex-col ${
        isOpen ? "w-[350px]" : "w-16"
      } ${!isOpen && "fixed"} relative`}
      ref={sidebarRef}
    >
      {/* Top Section */}

      {/* Sidebar Content */}
      <div className="flex-1 flex flex-col py-2"> {/* Added py-4 for top and bottom padding */}
        <div 
          ref={containerRef}
          className="relative  overflow-hidden"
        >
            {/* Overview section */}
            {isOpen && (
              <div className="px-6 mt-1">
                <p className="text-black font-bold text-2xl">Overview</p>
              </div>
            )}

            {/* Menu Items */}
            <ul className="mt-2 px-6 space-y-2">
              {menuItems.map((item, index) => (
                <li key={index} className="list-none">
                  <div className="flex items-center relative">
                    <Link
                      to={item.path}
                      className={`block text-lg font-bold w-full text-black cursor-pointer md:h-[70px] p-4 rounded-r-md hover:bg-[#152C24] hover:text-white transition-colors flex items-center ${
                        location.pathname === item.path || (location.pathname !== "/dashboards" && item.path === "/useraccount")
                          ? "bg-[#152C24] text-white"
                          : "bg-[#EDF0EF]"
                      }`}
                    >
                      {isOpen && <span>{item.name}</span>}
                    </Link>
                  </div>
                </li>
              ))}
            </ul>

            {/* Navigation section */}
            <ul className="mt-2 px-6 space-y-1.5">
              {navItems.map((item, index) => (
                <li key={index} className="list-none">
                  <Link
                    to={item.path}
                    className={`block text-lg font-bold cursor-pointer md:h-[54px] p-4 rounded-r-md hover:bg-[#152C24] hover:text-white transition-colors flex items-center ${
                      location.pathname === item.path
                        ? "bg-[#152C24] text-white"
                        : item.path === "/dashboards" 
                          ? "bg-[#2E453D] text-white mt-3 mb-4" // Updated Dashboard background color
                          : "bg-[#EDF0EF] text-black"
                    }`}
                  >
                    {isOpen && <span>{item.name}</span>}
                  </Link>
                </li>
              ))}
            </ul>
        </div>
      </div>


    </div>
  );
};

export default AdminSideBar;